// core/lib/apiClient.ts — fetch wrapper for the Django session-cookie API.
// Sends the session cookie (credentials:'include') and the CSRF token on unsafe methods.

export class ApiError extends Error {
  code: string;
  status: number;
  fields?: Record<string, unknown>;
  /** Optional reason string from sanction error bodies (account_suspended / account_banned). */
  reason?: string;
  /** Optional category string from sanction error bodies. */
  category?: string;
  constructor(
    code: string,
    status: number,
    fields?: Record<string, unknown>,
    reason?: string,
    category?: string,
  ) {
    super(code);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.fields = fields;
    this.reason = reason;
    this.category = category;
  }
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1] ?? '') : null;
}

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export async function api<T = unknown>(
  path: string,
  opts: { method?: string; body?: unknown } = {},
): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  if (opts.body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (UNSAFE.has(method)) {
    const token = readCookie('csrftoken');
    if (token) headers['X-CSRFToken'] = token;
  }
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'include',
    headers,
    body:
      opts.body === undefined
        ? undefined
        : isForm
          ? (opts.body as FormData)
          : JSON.stringify(opts.body),
  });
  const text = await res.text();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = undefined;
    }
  }
  if (!res.ok) {
    const d = (data ?? {}) as {
      code?: string;
      fields?: Record<string, unknown>;
      reason?: string;
      category?: string;
    };
    if (d.code === 'email_unverified' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('trux:email-unverified'));
    }
    // 401 = the server no longer knows this browser (the cookie expired, the session
    // was ended from another device, or the account was sanctioned). The shell decides
    // what to do: it ignores this for a guest and for the sign-in calls themselves,
    // and otherwise clears the stale session and sends the member to sign in again,
    // instead of leaving an app that looks signed in while every action fails.
    if (res.status === 401 && !path.startsWith('/auth/') && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('trux:session-expired'));
    }
    throw new ApiError(d.code ?? 'http_error', res.status, d.fields, d.reason, d.category);
  }
  return data as T;
}

export async function csrf(): Promise<void> {
  await api('/auth/csrf/');
}
